import { autoUpdate, flip, inline, offset, shift, useFloating } from '@floating-ui/react-dom'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { useOverlayTrigger } from '@react-aria/overlays'
import { useOverlayTriggerState } from '@react-stately/overlays'
import cx from 'classnames'
import { DateFormat } from 'common/PropertyForm/Types/Date'
import { formatDatePropertyValue } from 'common/PropertyValue/utils'
import { Button as AlmanacButton } from 'components/Button'
import { Calendar } from 'components/Calendar/Calendar'
import { Popover } from 'components/Popover/Popover'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { useKey } from 'react-use'
import {
  SelectPropertyEventDetails,
  selectPropertyEventName,
} from 'screens/Finder/components/FileTree/events/selectProperty'
import { useObjectRef } from 'utils/useObjectRef'
import Styles from './DatePicker.module.scss'
import { ButtonProps as AlmanacButtonProps } from 'components/Button/Types'
import { Pressable } from '@react-aria/interactions'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ChevronDown } from 'blaze-ui/icons'

interface DatePickerProps {
  value: string
  onChange: (value?: string) => void
  onDeleted?: () => void
  variant?: 'default' | 'table-date-picker' | 'filter'
  readOnly?: boolean
  onEditingStateChange?: (isEditing: boolean) => void
  onClick?: () => void
  dateFormat?: DateFormat
  wrapperClassName?: string
  size?: 'sm' | 'md' | 'lg'
}

export type DatePickerHandles = {
  setEditing: (editingState: boolean) => void
}

const Trigger = forwardRef<
  HTMLButtonElement,
  Omit<AlmanacButtonProps, 'variant' | 'size'> & {
    variant?: 'default' | 'table-date-picker' | 'filter'
    size: 'sm' | 'md' | 'lg'
  }
>((props, ref) => {
  if (props.variant === 'filter') {
    return <Button {...props} variant="tertiary" fullWidth ref={ref} endIcon={ChevronDown} />
  }

  return (
    <Pressable {...props} ref={ref}>
      <span className={Styles.trigger}>
        {props.children ? <Text variant="secondary">{props.children}</Text> : null}
      </span>
    </Pressable>
  )
})

export const DatePicker = forwardRef<DatePickerHandles, DatePickerProps>(
  (
    {
      value,
      onChange,
      variant = 'default',
      readOnly = false,
      onEditingStateChange,
      onClick,
      onDeleted,
      dateFormat,
      wrapperClassName,
      size = 'sm',
    },
    ref,
  ) => {
    const overlayRef = useObjectRef<HTMLElement>(null)
    const popoverRef = useRef<HTMLDivElement>()
    useImperativeHandle(ref, () => ({
      setEditing: (editingState: boolean) => {
        overlayState.setOpen(editingState)
      },
    }))

    const { x, y, reference, floating } = useFloating({
      placement: 'bottom',
      strategy: 'fixed',
      middleware: [offset(4), inline(), flip(), shift({ padding: 4 })],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
        })
      },
    })
    const overlayState = useOverlayTriggerState({
      onOpenChange: () => {
        floating(null)
      },
    })
    const { overlayProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, overlayRef)

    const popoverRefFn = (el: HTMLDivElement) => {
      floating(el)
      popoverRef.current = el
    }

    useKey(
      'Tab',
      (e) => {
        if (
          variant !== 'table-date-picker' ||
          !(e.target === popoverRef.current || popoverRef.current?.contains(e.target as Node))
        )
          return
        overlayState.close()
        // closing the dropdown will focus its cell, so we queue the focus change event on the next tick
        setTimeout(() => {
          document.body.dispatchEvent(
            new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
              bubbles: false,
              detail: e.shiftKey ? 'Previous' : 'Next',
            }),
          )
        }, 0)
      },
      undefined,
      [variant, popoverRef],
    )

    const buttonText = useMemo(() => {
      if (value.length > 0) {
        return formatDatePropertyValue(value, dateFormat)
      }
      if (['default', 'filter'].includes(variant)) {
        return 'Add value'
      }
      return ''
    }, [value, variant, dateFormat])

    useEffect(() => {
      onEditingStateChange?.(overlayState.isOpen)
    }, [onEditingStateChange, overlayState.isOpen])

    const clearDate = () => {
      onDeleted?.()
      overlayState.close()
    }

    const isVisible = x !== null && y !== null

    return (
      <div className={wrapperClassName}>
        <Trigger
          className={cx(
            {
              [Styles.placeholder]: value.length === 0,
              [Styles.uneditable]: readOnly,
            },
            Styles[variant],
          )}
          onPress={() => {
            if (!readOnly) {
              overlayState.open()
            }
            onClick?.()
          }}
          ref={reference}
          isDisabled={readOnly}
          size={size}
          variant={variant}
        >
          {buttonText}
        </Trigger>
        {overlayState.isOpen && (
          <FloatingPortal id="floating-ui-root">
            <Popover
              {...overlayProps}
              style={{ position: 'fixed', top: y || 0, left: x || 0, visibility: isVisible ? 'visible' : 'hidden' }}
              isOpen={overlayState.isOpen}
              onClose={overlayState.close}
              ref={popoverRefFn}
              isDismissable
            >
              <Calendar
                value={value === '' ? undefined : value}
                onChange={(value) => {
                  onChange(value)
                  overlayState.close()
                }}
              />
              {variant === 'table-date-picker' && value !== '' && (
                <div className={Styles.clearDateWrapper}>
                  <AlmanacButton className={Styles.clearDateBtn} onPress={clearDate} variant="tertiary">
                    Clear date
                  </AlmanacButton>
                </div>
              )}
            </Popover>
          </FloatingPortal>
        )}
      </div>
    )
  },
)
DatePicker.displayName = 'DatePicker'
